import axios from 'axios';
import { addParamsToQueryString } from '../functions/utils';
import { IApiResponse } from '../interfaces/IApiResponse';
import RestClient from './restClient';
import urls from './urls';

interface ConversationParams {
  [key: string]: any;
}

export interface IIdsConversations {
  Ids: number[];
}

export interface IPaginationMessage {
  lastSynchAt: number;
  pageSize: number;
}

class ConversationApi {
  static getConversations(params?: ConversationParams): Promise<IApiResponse> {
    const url = `${urls.CONVERSATION}?isGroupChatIncluded=true`;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getConversationsPined(params: ConversationParams): Promise<IApiResponse> {
    return axios.get(urls.CONVERSATION_PINNED, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
    });
  }

  static getConversationsDetails(peerXmppId: string, params: ConversationParams): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.NEW_CONVERSATION_DETAILS.replace('{receiverXmppId}', encodeURIComponent(peerXmppId)), params);
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static deleteConversation(receiverXmppid: string): Promise<IApiResponse> {
    const url = urls.CONVERSATION_DELETE.replace('{receiverXmppid}', encodeURIComponent(receiverXmppid));
    return axios.delete(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static deleteListConversations(idsConversation: IIdsConversations): Promise<IApiResponse> {
    const url = urls.LIST_CONVERSATION_DELETE;
    return axios.delete(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
      data: idsConversation,
    });
  }

  static deleteListConversationGroup(idsConversation: IIdsConversations): Promise<IApiResponse> {
    const url = urls.DELETE_LIST_GROUP_CONVERSATION;
    return axios.delete(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
      data: idsConversation,
    });
  }

  static deleteMessage(mgsId: string, params: ConversationParams): Promise<IApiResponse> {
    const url = urls.DELETE_MESSAGE.replace('{messageId}', encodeURIComponent(mgsId));
    return axios.delete(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
      data: params,
    });
  }

  static pinOrUnpinConversation({ pinned, xmppId = '' }: { pinned: boolean; xmppId: string }): Promise<IApiResponse> {
    if (pinned) {
      const url = urls.PIN_CONVERSATION;
      return axios.post(
        url,
        {
          pinXmppId: xmppId,
        },
        { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }
      );
    }

    const url = urls.UN_PIN_CONVERSATION;
    return axios.delete(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
      data: {
        pinXmppId: xmppId,
      },
    });
  }

  static getConversationUnreadCount(params: ConversationParams): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.CONVERSATION_UNREAD_COUNT, params);
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static checkIfCanSendMessage(userId: number): Promise<IApiResponse> {
    const url = urls.CONVERSATION_CHECK_CAN_SEND.replace('{userId}', encodeURIComponent(userId)) + `?random=${Math.random()}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getConversationBaseInfo(xmppId: string): Promise<IApiResponse> {
    const url = urls.CONVERSATION_GET_BASE_INFO.replace('{receiverXmppId}', encodeURIComponent(xmppId));
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getOrCreateConversation(conversation: ConversationParams): Promise<IApiResponse> {
    const url = urls.CONVERSATION;
    return axios.post(url, conversation, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static resetConversationUnreadCount(params: ConversationParams): Promise<IApiResponse> {
    const url = urls.CONVERSATION_UNREAD_COUNT;
    return axios.put(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getNewConversations(lastSynchAt: string, isGetNewConversation: boolean): Promise<IApiResponse> {
    const newConversation = isGetNewConversation ? 'true' : 'false';
    const url = `${urls.CONVERSATION_GET_NEW.replace('{lastSynchAt}', lastSynchAt).replace('{isGetNewConversation}', newConversation)}&isGroupChatIncluded=true`;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static updateStatementRead(params: ConversationParams): Promise<IApiResponse> {
    const url = urls.PATCH_USER_VCARD;
    return axios.patch(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static patchIgnoreConversation(id: string, isIgnored: boolean): Promise<IApiResponse> {
    const url = urls.PATCH_IGNORE_CONVERSATION;
    const params = {
      buddyXmppId: id,
      isIgnored: !!isIgnored,
    };
    return axios.patch(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getSharedMedia(receiverXmppid: string, { lastSynchAt, pageSize }: IPaginationMessage): Promise<IApiResponse> {
    const url = `${urls.GET_SHARED_MEDIA.replace('{receiverXmppId}', encodeURIComponent(receiverXmppid))}?lastSynchAt=${lastSynchAt}&pageSize=${pageSize}`;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getSharedMediaByDirection(
    receiverXmppid: string,
    pageSize: number,
    referenceMediaTime: string,
    direction: string
  ): Promise<IApiResponse> {
    const url = `${urls.GET_SHARED_MEDIA_BY_DIRECTION.replace('{receiverXmppId}', encodeURIComponent(receiverXmppid))}?referenceMediaTime=${referenceMediaTime}&fetchDirection=${direction}&pageSize=${pageSize}`;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static deleteSharedMedia(receiverXmppid: string, mediaIds: string[]): Promise<IApiResponse> {
    const url = `${urls.DELETE_SHARED_MEDIA.replace('{receiverXmppId}', encodeURIComponent(receiverXmppid))}`;
    return axios.post(
      url,
      { mediaIds },
      {
        baseURL: urls.BASE_URL,
        headers: { isAnonymous: false },
      }
    );
  }

  // static createUserPhotoBanner(formData: FormData): Promise<IApiResponse> {
  //   const restClient = new RestClient();
  //   restClient.setBaseUrl(urls.BASE_RHPURL);
  //   const url = urls.CREATE_USER_PHOTO_BANNER;
  //   return axios.post(url, formData, {
  //     baseURL: urls.BASE_RHPURL,
  //     headers: { isAnonymous: false },
  //   });
  // }
  static createUserPhotoBanner(formData: FormData): Promise<IApiResponse> {
    const restClient = new RestClient();
    restClient.setBaseUrl(urls.BASE_RHPURL);
    const url = urls.CREATE_USER_PHOTO_BANNER;
    // @ts-ignore
    return restClient.execute(url, 'POST', formData, false, false, false, true).then(res => res);
  }
  static sendMassMsgEventGroupChat(params: { eventId: number | string; receivers: string[]; message: string }): Promise<IApiResponse> {
    const url = `${urls.SEND_EVENT_GROUP_CHAT_MASS_MSG}`;
    return axios.post(url, params, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
    });
  }
  static getUsersStatus(uIds: number[]): Promise<IApiResponse> {
    let url = `${urls.GET_USER_CONVERSATION_STATUS}`;
    if (uIds?.length > 0) {
      let _uIds = uIds.join();
      url = url + '?uIds=' + _uIds;
    }
    return axios.get(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
    });
  }
}

export default ConversationApi;
